import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'

export interface FilterOption {
  value: string
  label: string
  icon?: React.ReactNode
}

/** Long enough that finding an option by typing beats scanning for it. */
const SEARCHABLE_FROM = 8

/**
 * A filter that picks any number of values from a list. The trigger is dashed
 * until something is chosen, then shows how many.
 */
export function FilterChip({
  label,
  icon,
  options,
  selected,
  onChange,
}: {
  label: string
  icon: React.ReactNode
  options: readonly FilterOption[]
  selected: readonly string[]
  onChange: (selected: string[]) => void
}) {
  const { t } = useTranslation()
  const count = selected.length

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('bg-background', count === 0 ? 'border-dashed' : 'on-surface')}
        >
          {icon}
          {label}
          {count > 0 && (
            <Badge variant="secondary" className="px-1.5">
              {count}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-0">
        <Command>
          {options.length >= SEARCHABLE_FROM && <CommandInput placeholder={label} />}
          <CommandList>
            <CommandEmpty>{t('filterChip.noOptions')}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const on = selected.includes(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    aria-checked={on}
                    onSelect={() => {
                      onChange(
                        on
                          ? selected.filter((value) => value !== option.value)
                          : [...selected, option.value],
                      )
                    }}
                  >
                    <Check className={cn(!on && 'invisible')} />
                    {option.icon}
                    <span className="truncate">{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
